<script setup>
import { computed, ref, watch } from 'vue';

import CustomModal from '@/views/common/text-editors/CustomModal.vue';
import LoadingButton from '@/views/common/components/LoadingButton.vue';
import {
  BULK_CV_ACTIONS,
  BULK_CV_STATUS_META
} from '@/composables/recruitments/useBulkCvBatches';
import useAuth from '@/composables/auth/useAuth';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  item: {
    type: Object,
    default: () => null
  },
  batch: {
    type: Object,
    default: () => null
  },
  loading: {
    type: Boolean,
    default: false
  }
});
const emit = defineEmits(['update:modelValue', 'resolve']);
const { hasAccess } = useAuth();
const canViewCandidate = computed(
  () =>
    hasAccess({ name: 'candidate', option: 'view_global' }) ||
    hasAccess({ name: 'candidate', option: 'view_own' })
);
const canViewApplicant = computed(() =>
  hasAccess({ name: 'applicant', option: 'view' })
);

const selectedCandidateId = ref(null);
const pendingAction = ref(null);
const hasSelectedJob = computed(() => Boolean(props.batch?.job?.id));

watch(
  () => [props.modelValue, props.item?.id],
  () => {
    pendingAction.value = null;
    selectedCandidateId.value =
      props.item?.matches?.length === 1
        ? props.item.matches[0]?.candidate?.id
        : null;
  },
  { immediate: true }
);

const candidateName = (candidate) =>
  [candidate?.first_name, candidate?.middle_name, candidate?.last_name]
    .filter(Boolean)
    .join(' ') || 'Unnamed candidate';

const selectedMatch = computed(() =>
  props.item?.matches?.find(
    (match) => String(match.candidate?.id) === String(selectedCandidateId.value)
  )
);

const scenario = computed(() => {
  if (props.item?.scenario !== 'multiple_matches') return props.item?.scenario;
  if (!selectedMatch.value) return 'multiple_matches';
  return (
    selectedMatch.value.scenario ||
    (selectedMatch.value.is_existing_applicant
      ? 'existing_applicant'
      : 'existing_candidate')
  );
});

const scenarioTitle = computed(() => {
  if (!hasSelectedJob.value && scenario.value === 'existing_applicant') {
    return 'Existing Candidate Found';
  }
  const labels = {
    existing_candidate: 'Existing Candidate Found',
    existing_applicant: 'Existing Applicant Found',
    multiple_matches: 'Multiple Possible Matches'
  };
  return labels[scenario.value] || 'Resolve Candidate Match';
});

const scenarioMessage = computed(() => {
  if (!hasSelectedJob.value) {
    if (scenario.value === 'existing_candidate') {
      return 'This CV matches an existing candidate. Choose whether to update that profile, keep it unchanged, create a separate candidate, or skip this CV. No applicant will be created.';
    }
    return 'Select the matching candidate profile before choosing an action. This candidate-only batch will not create applicants.';
  }
  if (scenario.value === 'existing_applicant') {
    return `This candidate is already an applicant for ${
      props.batch?.job?.position || 'the selected job'
    }. No second applicant will be created automatically.`;
  }
  if (scenario.value === 'existing_candidate') {
    return `This candidate is not currently an applicant for ${
      props.batch?.job?.position || 'the selected job'
    }. Choose how to resolve the profile before adding an applicant.`;
  }
  return 'Select the matching candidate profile before choosing an action.';
});

const actionDefinitions = computed(() => ({
  [BULK_CV_ACTIONS.OVERRIDE]: {
    label: hasSelectedJob.value
      ? 'Overwrite Candidate and Add to Job'
      : 'Overwrite Candidate',
    shortLabel: 'Overwrite candidate',
    variant: 'danger',
    icon: 'refresh-cw',
    description: hasSelectedJob.value
      ? 'Update the selected candidate from this CV, retain the Candidate ID and add that candidate to the job.'
      : 'Update the selected candidate from this CV and retain the Candidate ID. No applicant will be created.',
    confirmation: hasSelectedJob.value
      ? 'The existing candidate profile and current CV will be updated. Historical records must remain attached to the same Candidate ID.'
      : 'The existing candidate profile and current CV will be updated. No applicant or job relationship will be created.'
  },
  [BULK_CV_ACTIONS.DUPLICATE]: {
    label: 'Create Duplicate Candidate',
    shortLabel: 'Create duplicate',
    variant: 'warning',
    icon: 'copy',
    description: hasSelectedJob.value
      ? 'Create a separate candidate profile and a new applicant for this job.'
      : 'Create a separate candidate profile without creating an applicant.',
    confirmation: hasSelectedJob.value
      ? 'A new candidate profile and applicant will be created even though a possible match exists. This can create duplicate person records.'
      : 'A new candidate profile will be created even though a possible match exists. No applicant will be created.'
  },
  [BULK_CV_ACTIONS.USE_EXISTING]: {
    label: hasSelectedJob.value
      ? 'Use Existing Candidate and Add to Job'
      : 'Use Existing Candidate',
    shortLabel: 'Use existing',
    variant: 'primary',
    icon: 'user-check',
    description: hasSelectedJob.value
      ? 'Keep the existing profile and CV unchanged, then create the applicant using its Candidate ID.'
      : 'Keep the existing candidate profile and CV unchanged, then complete this item without creating an applicant.',
    confirmation: hasSelectedJob.value
      ? 'The existing candidate profile will remain unchanged and will be added to the selected job.'
      : 'The existing candidate profile will remain unchanged. No applicant or job relationship will be created.'
  },
  [BULK_CV_ACTIONS.UPDATE_APPLICANT]: {
    label: 'Update Candidate and Existing Applicant CV',
    shortLabel: 'Update existing applicant',
    variant: 'primary',
    icon: 'refresh-cw',
    description:
      'Update the candidate and CV while retaining the existing Candidate and Applicant IDs.',
    confirmation:
      'The existing candidate and applicant CV will be updated. No additional applicant will be created.'
  },
  [BULK_CV_ACTIONS.KEEP_APPLICANT]: {
    label: 'Keep Existing Applicant Without Changes',
    shortLabel: 'Keep existing applicant',
    variant: 'success',
    icon: 'check-circle',
    description:
      'Leave both candidate and applicant unchanged and complete this upload item.',
    confirmation:
      'The uploaded CV will not change the existing candidate or applicant.'
  },
  [BULK_CV_ACTIONS.SKIP]: {
    label: 'Skip This CV',
    shortLabel: 'Skip',
    variant: 'secondary',
    icon: 'slash',
    description: hasSelectedJob.value
      ? 'Do not create or update a candidate or applicant for this CV.'
      : 'Do not create or update a candidate for this CV.',
    confirmation: hasSelectedJob.value
      ? 'This CV will be skipped. No candidate or applicant changes will be made.'
      : 'This CV will be skipped. No candidate changes will be made.'
  }
}));

const pendingConfirmation = computed(() => {
  if (
    hasSelectedJob.value &&
    pendingAction.value === BULK_CV_ACTIONS.DUPLICATE &&
    scenario.value === 'existing_applicant'
  ) {
    return `This candidate is already an applicant for ${
      props.batch?.job?.position || 'the selected job'
    }. Continuing creates both a second candidate profile and another applicant.`;
  }
  return actionDefinitions.value[pendingAction.value]?.confirmation || '';
});

const scenarioActions = computed(() => {
  const actions = !hasSelectedJob.value
    ? [
        BULK_CV_ACTIONS.OVERRIDE,
        BULK_CV_ACTIONS.USE_EXISTING,
        BULK_CV_ACTIONS.DUPLICATE,
        BULK_CV_ACTIONS.SKIP
      ]
    : scenario.value === 'existing_applicant'
    ? [
        BULK_CV_ACTIONS.UPDATE_APPLICANT,
        BULK_CV_ACTIONS.KEEP_APPLICANT,
        BULK_CV_ACTIONS.DUPLICATE,
        BULK_CV_ACTIONS.SKIP
      ]
    : [
        BULK_CV_ACTIONS.OVERRIDE,
        BULK_CV_ACTIONS.USE_EXISTING,
        BULK_CV_ACTIONS.DUPLICATE,
        BULK_CV_ACTIONS.SKIP
      ];
  const allowed = selectedMatch.value?.actions_authoritative
    ? selectedMatch.value.allowed_actions
    : props.item?.allowed_actions || [];
  return actions
    .filter((action) => allowed.includes(action))
    .map((action) => ({ action, ...actionDefinitions.value[action] }));
});

const requiresMatch = computed(() =>
  ['existing_candidate', 'existing_applicant', 'multiple_matches'].includes(
    props.item?.scenario
  )
);
const missingMatchData = computed(
  () => requiresMatch.value && !props.item?.matches?.length
);

const canChooseAction = (action) =>
  action === BULK_CV_ACTIONS.SKIP ||
  !requiresMatch.value ||
  Boolean(selectedCandidateId.value);

const selectAction = (action) => {
  if (!canChooseAction(action) || props.loading) return;
  pendingAction.value = action;
};

const confirmAction = () => {
  if (!pendingAction.value || props.loading) return;
  emit('resolve', {
    action: pendingAction.value,
    existing_candidate_id: selectedCandidateId.value || undefined,
    confirmed: true
  });
};

const close = () => {
  if (props.loading) return;
  emit('update:modelValue', false);
};

const confidenceLabel = (confidence) => {
  if (confidence == null || confidence === '') return '';
  const number = Number(confidence);
  if (Number.isNaN(number)) return String(confidence);
  return `${number <= 1 ? Math.round(number * 100) : Math.round(number)}%`;
};
</script>

<template>
  <CustomModal
    :model-value="modelValue"
    :title="scenarioTitle"
    root-class="bulk-cv-modal-layer"
    backdrop
    width-class="col-xl-9 col-lg-10 col-12"
    @update:model-value="close"
  >
    <div
      v-if="item"
      class="p-4 resolution-modal"
    >
      <div class="d-flex flex-wrap justify-content-between gap-2 mb-3">
        <div>
          <div class="text-12px text-muted">Uploaded CV</div>
          <div class="uploaded-cv-name fw-bold">{{ item.name }}</div>
        </div>
        <div class="text-end">
          <div class="text-12px text-muted">Processing mode</div>
          <div class="fw-bold">
            {{ batch?.job?.position || 'Candidates only' }}
          </div>
        </div>
      </div>

      <div class="rounded border p-3 mb-3 bg-body">
        <div class="fw-bold mb-1">{{ scenarioTitle }}</div>
        <div class="text-13px">{{ scenarioMessage }}</div>
      </div>

      <div
        v-if="item.parsed_candidate"
        class="parsed-profile rounded border p-3 mb-3"
      >
        <div class="text-11px text-muted text-uppercase mb-1">
          Parsed from this CV
        </div>
        <div class="fw-bold">{{ candidateName(item.parsed_candidate) }}</div>
        <div class="d-flex flex-wrap gap-3 text-12px mt-1">
          <span v-if="item.parsed_candidate.candidate_email">
            {{ item.parsed_candidate.candidate_email }}
          </span>
          <span v-if="item.parsed_candidate.candidate_phone">
            {{ item.parsed_candidate.candidate_phone }}
          </span>
        </div>
      </div>

      <div
        v-if="item.matches?.length"
        class="mb-4"
      >
        <div class="fw-bold mb-2">
          {{
            item.matches.length > 1
              ? 'Select the correct match'
              : 'Matched profile'
          }}
        </div>
        <div class="table-responsive rounded border">
          <table class="table table-hover align-middle mb-0 match-table">
            <thead>
              <tr>
                <th class="selection-column">Select</th>
                <th>Candidate</th>
                <th>Contact</th>
                <th>Employer / Location</th>
                <th>Match evidence</th>
                <th>
                  {{ hasSelectedJob ? 'Job relationship' : 'Candidate status' }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="match in item.matches"
                :key="match.candidate?.id"
                :class="{
                  'selected-match':
                    String(selectedCandidateId) === String(match.candidate?.id)
                }"
                class="cursor-pointer"
                @click="selectedCandidateId = match.candidate?.id"
              >
                <td
                  class="text-center"
                  data-label="Select"
                >
                  <input
                    v-model="selectedCandidateId"
                    type="radio"
                    :value="match.candidate?.id"
                    :aria-label="`Select candidate ${match.candidate?.id}`"
                  />
                </td>
                <td data-label="Candidate">
                  <RouterLink
                    v-if="
                      match.candidate?.id &&
                      !match.candidate?.restricted &&
                      canViewCandidate
                    "
                    :to="
                      match.candidate.detail_url || {
                        name: 'recruitments-candidates-details',
                        params: { id: match.candidate.id }
                      }
                    "
                    target="_blank"
                    @click.stop
                  >
                    {{ candidateName(match.candidate) }}
                  </RouterLink>
                  <span v-else>Candidate profile</span>
                  <div class="text-11px text-muted">
                    CC-{{ match.candidate?.id }}
                  </div>
                </td>
                <td
                  class="text-12px"
                  data-label="Contact"
                >
                  <template
                    v-if="canViewCandidate && !match.candidate?.restricted"
                  >
                    <div>{{ match.candidate?.candidate_email || '—' }}</div>
                    <div>{{ match.candidate?.candidate_phone || '—' }}</div>
                  </template>
                  <span
                    v-else
                    class="text-muted"
                    >Restricted</span
                  >
                </td>
                <td
                  class="text-12px"
                  data-label="Employer / location"
                >
                  <template
                    v-if="canViewCandidate && !match.candidate?.restricted"
                  >
                    <div>{{ match.candidate?.current_company || '—' }}</div>
                    <div class="text-muted">
                      {{ match.candidate?.candidate_location || '—' }}
                    </div>
                  </template>
                  <span
                    v-else
                    class="text-muted"
                    >Restricted</span
                  >
                </td>
                <td
                  class="text-12px"
                  data-label="Match evidence"
                >
                  <div class="d-flex flex-wrap gap-1">
                    <span
                      v-if="match.match_type"
                      class="badge badge-outline-info"
                    >
                      {{ match.match_type.replace(/_/g, ' ') }}
                    </span>
                    <span
                      v-if="confidenceLabel(match.confidence)"
                      class="badge badge-outline-secondary"
                    >
                      {{ confidenceLabel(match.confidence) }}
                    </span>
                    <span
                      v-for="(evidence, index) in match.evidence"
                      :key="`${evidence.field || evidence}-${index}`"
                      class="badge badge-outline-secondary"
                    >
                      {{ evidence.field || evidence
                      }}<template
                        v-if="
                          canViewCandidate &&
                          !match.candidate?.restricted &&
                          evidence.value !== undefined &&
                          evidence.value !== null
                        "
                        >: {{ evidence.value }}</template
                      >
                    </span>
                  </div>
                </td>
                <td
                  class="text-12px"
                  :data-label="
                    hasSelectedJob ? 'Job relationship' : 'Candidate status'
                  "
                >
                  <template v-if="hasSelectedJob">
                    <span
                      v-if="match.is_existing_applicant"
                      class="badge badge-outline-warning"
                      >Already an applicant</span
                    >
                    <span
                      v-else
                      class="badge badge-outline-success"
                      >Not applied</span
                    >
                  </template>
                  <span
                    v-else
                    class="badge badge-outline-info"
                  >
                    Existing candidate
                  </span>
                  <div
                    v-if="
                      hasSelectedJob &&
                      match.applicant?.stage &&
                      !match.applicant?.restricted &&
                      canViewApplicant
                    "
                    class="text-muted mt-1"
                  >
                    Stage:
                    {{ match.applicant.stage?.title || match.applicant.stage }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="!pendingAction">
        <div class="fw-bold mb-2">Choose an action</div>
        <div
          v-if="missingMatchData"
          class="alert alert-danger py-2 text-13px"
        >
          Match details are incomplete. Refresh the batch before resolving this
          CV.
        </div>
        <div
          v-else-if="requiresMatch && !selectedCandidateId"
          class="alert alert-warning py-2 text-13px"
        >
          Select a candidate match to update or use a profile. You can skip this
          CV without selecting one.
        </div>
        <div
          v-if="scenarioActions.length"
          class="row g-2"
        >
          <div
            v-for="action in scenarioActions"
            :key="action.action"
            class="col-lg-6"
          >
            <button
              type="button"
              class="action-card text-start w-100 rounded border p-3 bg-card"
              :disabled="!canChooseAction(action.action) || loading"
              @click="selectAction(action.action)"
            >
              <div class="d-flex align-items-center mb-1">
                <FeatherIcon
                  :type="action.icon"
                  size="16"
                  :class="`text-${action.variant}`"
                  class="me-2"
                />
                <span class="fw-bold">{{ action.label }}</span>
              </div>
              <div class="text-12px text-muted">{{ action.description }}</div>
            </button>
          </div>
        </div>
        <div
          v-else
          class="alert alert-warning py-2 mb-0"
        >
          You do not have permission for any action on this item. Contact an
          administrator.
        </div>
      </div>

      <div
        v-else
        class="confirmation-panel rounded border p-3"
      >
        <div class="d-flex align-items-start">
          <FeatherIcon
            type="alert-triangle"
            size="20"
            class="text-warning me-2 mt-1 flex-shrink-0"
          />
          <div>
            <div class="fw-bold mb-1">Confirm decision</div>
            <div class="text-13px">
              {{ pendingConfirmation }}
            </div>
          </div>
        </div>
        <div class="flex-end mt-3">
          <button
            v-if="
              pendingAction === BULK_CV_ACTIONS.SKIP &&
              scenario === 'existing_candidate' &&
              item.allowed_actions.includes(BULK_CV_ACTIONS.USE_EXISTING)
            "
            class="btn btn-outline-primary me-auto"
            :disabled="loading"
            @click="pendingAction = BULK_CV_ACTIONS.USE_EXISTING"
          >
            Add Existing Candidate Instead
          </button>
          <button
            class="btn btn-outline-secondary me-2"
            :disabled="loading"
            @click="pendingAction = null"
          >
            Back
          </button>
          <LoadingButton
            :title="actionDefinitions[pendingAction]?.shortLabel || 'Confirm'"
            :variant="actionDefinitions[pendingAction]?.variant || 'primary'"
            :loading="loading"
            @submit="confirmAction"
          />
        </div>
      </div>

      <div
        v-if="item.error"
        class="alert alert-danger mt-3 mb-0"
      >
        <div class="fw-bold">{{ BULK_CV_STATUS_META.failed.label }}</div>
        <div class="text-12px">{{ item.error.message }}</div>
      </div>
    </div>
  </CustomModal>
</template>

<style lang="scss" scoped>
.resolution-modal {
  min-height: 360px;
}

.selection-column {
  width: 64px;
  text-align: center;
}

.match-table {
  min-width: 920px;
}

.selected-match {
  box-shadow: inset 3px 0 0 var(--bs-info);
}

.action-card {
  min-height: 105px;
  transition: border-color 0.15s ease, transform 0.15s ease;
}

.action-card:not(:disabled):hover {
  border-color: var(--bs-info) !important;
  transform: translateY(-1px);
}

.action-card:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.confirmation-panel {
  border-left: 3px solid var(--bs-warning) !important;
}
</style>
